import React, { ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  List,
  ListItem,
  IconButton,
} from "@mui/material";

import styled from "@emotion/styled";
import { MenuOpen, Menu } from "@mui/icons-material";
import { useSideBar } from "hooks/useSidebar";

import logoUVerify from "assets/img/icon_uverify_gray.png";

function extractText(node: React.ReactNode): string {
  if (typeof node === "string") {
    return node;
  } else if (React.isValidElement(node)) {
    return extractText(node.props.children);
  } else if (Array.isArray(node)) {
    return node.map(extractText).join(" ");
  }
  return "";
}

export function generateId(text: React.ReactNode): string {
  const extractedText = extractText(text);
  return extractedText
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .trim();
}

const Content = styled(Box)`
  background-color: #f5f5f7;
  width: 100%;
  overflow-y: auto;

  scrollbar-width: thin;
  scrollbar-color: #c5c5c5 transparent;

  /* Works on Chrome, Edge, and Safari */
  *::-webkit-scrollbar {
    width: 10px;
  }

  *::-webkit-scrollbar-track {
    background: transparent;
  }

  *::-webkit-scrollbar-thumb {
    padding: 4px;
    background-color: #c5c5c5;
    background-size: contain;
    border-radius: 20px;
    border: 3px solid transparent;
  }
`;

const Logo = styled.span<{ image: string }>`
  position: relative;
  height: 8rem;
  width: 8rem;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  background-image: ${(props) => `url(${props.image})`};
`;

interface TutorialContentProps {
  markdown: string;
}

const TutorialContent: React.FC<TutorialContentProps> = ({ markdown }) => {
  const { collapsedDocumentation, setCollapsedDocumentation } = useSideBar();
  const [logoUV] = React.useState(logoUVerify);

  return (
    <Content>
      <IconButton
        aria-label="delete"
        size="large"
        sx={{ position: "absolute", zIndex: "2", margin: " 1rem" }}
        onClick={() => setCollapsedDocumentation((value) => !value)}
      >
        {collapsedDocumentation ? <Menu /> : <MenuOpen />}
      </IconButton>
      <Box sx={{ padding: "2rem 5rem", height: markdown ? "auto" : "100%" }}>
        <ReactMarkdown
          children={markdown}
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            h1: ({ children }) => {
              const id = generateId(children);
              return (
                <Typography
                  id={id}
                  variant="h3"
                  gutterBottom
                  style={{
                    fontWeight: "semibold",
                    fontSize: "2rem",
                    scrollMarginTop: "20px",
                  }}
                >
                  {children}
                </Typography>
              );
            },
            h2: ({ children }) => {
              const id = generateId(children);
              return (
                <Typography
                  id={id}
                  variant="h4"
                  gutterBottom
                  style={{
                    fontWeight: "bold",
                    fontSize: "1.5rem",
                    scrollMarginTop: "20px",
                  }}
                >
                  {children}
                </Typography>
              );
            },
            h3: ({ children }) => {
              const id = generateId(children);
              return (
                <Typography
                  id={id}
                  variant="h5"
                  gutterBottom
                  style={{
                    fontWeight: "bold",
                    color: "#555",
                    fontSize: "1.25rem",
                    scrollMarginTop: "20px",
                  }}
                >
                  {children}
                </Typography>
              );
            },
            p: ({ children }) => (
              <Typography variant="body1" gutterBottom>
                {children}
              </Typography>
            ),
            ul: ({ children }) => (
              <List style={{ paddingLeft: "20px" }}>{children}</List>
            ),
            li: ({ children }) => (
              <ListItem style={{ display: "list-item", paddingLeft: "10px" }}>
                {children}
              </ListItem>
            ),
            table: ({ children }) => (
              <TableContainer component={Paper} style={{ marginTop: "20px" }}>
                <Table>{children}</Table>
              </TableContainer>
            ),
            thead: ({ children }) => <TableHead>{children}</TableHead>,
            tbody: ({ children }) => <TableBody>{children}</TableBody>,
            tr: ({ children }) => <TableRow>{children}</TableRow>,
            th: ({ children }) => (
              <TableCell
                style={{ fontWeight: "bold", backgroundColor: "#f4f4f4" }}
              >
                {children}
              </TableCell>
            ),
            td: ({ children }) => <TableCell>{children}</TableCell>,
            img: ({ src, alt }) => (
              <Box
                component="img"
                src={src}
                alt={alt}
                sx={{
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: 2,
                  display: "block",
                  margin: "16px auto",
                }}
              />
            ),
          }}
        />
        {!markdown && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Logo image={logoUV} />
            <Typography
              variant="h4"
              gutterBottom
              style={{
                fontWeight: "bold",
                fontSize: "1.5rem",
              }}
            >
              Bem vindo a Documentação do U-Verify
            </Typography>
            <Typography
              variant="caption"
              gutterBottom
              style={{
                fontWeight: "bold",
                fontSize: "1rem",
                textAlign: "center",
              }}
            >
              Selecione a esquerda algum tópico que deseja saber mais e navega
              entre os conteúdos para maior entendimento sobre a plataforma!
            </Typography>
          </Box>
        )}
      </Box>
    </Content>
  );
};

export default TutorialContent;
